import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Link } from '@one-thd/sui-atomic-components';
import Filter from './Filter';
import SelectedFilter from './SelectedFilter';

const FILTER_WRAPPER_ID = 'gcFilters';

const FiltersWrapper = (props) => {
  const { onChange, total, selectedFilters, allFilters } = props;
  const filterTypes = Object.keys(allFilters);
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  const updateFilters = (type, newVal) => {
    const _selectedFilters = {
      ...selectedFilters,
      [type]: newVal
    };

    if (!newVal || newVal.length === 0) {
      delete _selectedFilters[type];
    }
    onChange(_selectedFilters);

    if (isMobile) {
      const anchor = document.getElementById(FILTER_WRAPPER_ID);
      try {
        anchor.scrollIntoView({ behavior: 'smooth' });
      } catch (err) {
        /* */
      }
    }
  };

  const handleFiltersChange = (type, label) => {
    let _selected = selectedFilters?.[type];
    if (_selected && _selected.length) {
      const isFilterSelected = _selected.findIndex((ft) => ft === label) > -1;
      if (isFilterSelected) {
        _selected = _selected.filter((ft) => ft !== label);
      } else {
        _selected = [..._selected, label];
      }
    } else {
      _selected = [label];
    }
    updateFilters(type, _selected);
  };

  const handleRemoveFilters = (val, filterType) => {
    if (val && filterType) {
      const _selected = selectedFilters?.[filterType].filter((ft) => ft !== val);
      updateFilters(filterType, _selected);
    }
  };

  const handleClearAll = (event) => {
    event.preventDefault();
    onChange(null);
  };

  const renderSelectedFilters = () => {
    if (!selectedFilters || !Object.keys(selectedFilters).length) {
      return null;
    }

    const totalSelected = Object.values(selectedFilters).reduce((accumulator, currentVal) => {
      return accumulator + currentVal.length;
    }, 0);

    const listItems = Object.keys(selectedFilters).map((filterType) => {
      return selectedFilters[filterType].map((filter) => (
        <SelectedFilter
          filterType={filterType}
          label={filter}
          onClear={handleRemoveFilters}
          key={filter}
        />
      ));
    });

    return (
      <>
        {listItems}
        {totalSelected >= 4 && (
          <span className="sui-flex sui-items-center">
            <Link component="button" onClick={handleClearAll} data-testid="filter-clear-all">
              Clear All
            </Link>
          </span>
        )}
      </>
    );
  };

  return (
    <div className="sui-grid sui-grid-cols-6 sui-gap-2 sui-p-2">
      <div
        className="sui-col-span-6 sui-w-full sm:sui-grid sui-grid-cols-6"
        id={FILTER_WRAPPER_ID}
        data-testid="filters-wrapper"
      >
        <div className="sui-col-span-5 sui-flex sui-gap-4 sui-overflow-y-auto">
          {filterTypes.map((filterType) => {
            return (
              <Filter
                key={filterType}
                type={filterType}
                filters={allFilters[filterType]}
                selectedFilters={selectedFilters?.[filterType]}
                onChange={handleFiltersChange}
                containerId={FILTER_WRAPPER_ID}
                isMobile={isMobile}
              />
            );
          })}
        </div>
        <span className="sui-col-span-1 sm:sui-flex sui-hidden sui-self-end sui-justify-self-end sui-font-bold">
          {`${total} ${total > 1 ? 'Results' : 'Result'}`}
        </span>
      </div>
      <div
        className="sui-col-span-6 sui-flex sui-flex-wrap sui-gap-4"
        data-testid="selected-filters"
      >
        {isMobile && (
          <span className="sui-flex sui-font-bold sui-items-center">
            {`${total} ${total > 1 ? 'Results' : 'Result'}`}
          </span>
        )}
        {renderSelectedFilters()}
      </div>
      {total === 0 && (
        <div className="sui-col-span-6" data-testid="filter-no-results">
          Sorry, there are no products available.
        </div>
      )}
    </div>
  );
};

FiltersWrapper.propTypes = {
  onChange: PropTypes.func,
  total: PropTypes.number,
  selectedFilters: PropTypes.oneOfType([PropTypes.object]),
  allFilters: PropTypes.oneOfType([PropTypes.object])
};

FiltersWrapper.defaultProps = {
  onChange: () => {},
  total: 0,
  selectedFilters: null,
  allFilters: null
};

export default FiltersWrapper;
